import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
  Box, Button, Typography, Paper, CircularProgress, Chip, Alert,
  Table, TableHead, TableBody, TableRow, TableCell, TableContainer, LinearProgress,
} from '@mui/material';
import { num, baht, usdAsBaht, FX_LABEL, date, dateTime, REVENUE_TYPES, MARKETPLACE_LABEL } from './format';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const REPORT_URL = `${BACKEND_URL}/api/report/platform-customers/customers`;

const WALLET_TYPE = { deposit: 'เติมเงิน', withdraw: 'ถอนเงิน', onhold: 'กันเงิน', payment: 'จ่าย', refund: 'คืนเงิน' };
const COUPON_STATUS = { active: 'ยังไม่ใช้', used: 'ใช้แล้ว', expired: 'หมดอายุ', cancelled: 'ยกเลิก' };
const SHIPPING_LABEL = { air: 'แอร์', sea: 'เรือ', unassigned: 'ยังไม่มีรอบ' };
const TIMELINE_COLOR ={ order: '#2E8B57', purchase: '#2F62D9', wallet: '#2E8B57', coupon: '#E07B1F', negotiation: '#A9BEEB' };

function Kpi({ label, value, sub }) {
  return (
    <Paper variant="outlined" sx={{ p: 2, flex: '1 1 190px', minWidth: 0 }}>
      <Typography variant="body2" color="text.secondary">{label}</Typography>
      <Typography variant="h5" fontWeight="bold" sx={{ fontVariantNumeric: 'tabular-nums' }}>{value}</Typography>
      {sub && <Typography variant="caption" color="text.secondary">{sub}</Typography>}
    </Paper>
  );
}

function Mini({ label, value, sub }) {
  return (
    <Box sx={{ minWidth: 0 }}>
      <Typography variant="caption" color="text.secondary">{label}</Typography>
      <Typography fontWeight="bold" sx={{ fontVariantNumeric: 'tabular-nums' }}>{value}</Typography>
      {sub && <Typography variant="caption" color="text.secondary">{sub}</Typography>}
    </Box>
  );
}

/** One platform customer: who, how they use the site, and every baht. */
const PlatformCustomerDetail = ({ userId, range, onBack }) => {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  const toggleTestAccount = async () => {
    const next = !data.customer.isTestAccount;
    const msg = next
      ? 'ตั้งเป็นบัญชีทดสอบ? ข้อมูลของลูกค้าคนนี้จะไม่ถูกนับในรายงานนี้'
      : 'นำกลับมานับในรายงาน?';
    if (!window.confirm(msg)) return;
    setSaving(true);
    try {
      await axios.patch(`${REPORT_URL}/${userId}/test-account`, { isTestAccount: next });
      setData({ ...data, customer: { ...data.customer, isTestAccount: next } });
    } catch (err) {
      window.alert(err.response?.data?.message || 'บันทึกไม่สำเร็จ');
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    let cancelled = false;
    setData(null);
    setError('');
    axios.get(`${REPORT_URL}/${userId}`, { params: range })
      .then((res) => { if (!cancelled) setData(res.data); })
      .catch((err) => { if (!cancelled) setError(err.response?.data?.message || 'โหลดข้อมูลลูกค้าไม่สำเร็จ'); });
    return () => { cancelled = true; };
  }, [userId, range]);

  const back = <Button onClick={onBack} sx={{ mb: 2 }}>← ลูกค้าทั้งหมด</Button>;
  if (error) return <Box sx={{ width: '100%', maxWidth: 1360, p: 2 }}>{back}<Alert severity="error">{error}</Alert></Box>;
  if (!data) return <Box sx={{ width: '100%', maxWidth: 1360, p: 2 }}>{back}<CircularProgress /></Box>;

  const { customer: c, lifetime, usage, wallet } = data;
  const quota = usage.negotiationQuota;
  const tr = usage.translations;

  return (
    <Box sx={{ width: '100%', maxWidth: 1360, p: { xs: 1, sm: 2 } }}>
      {back}

      <Paper variant="outlined" sx={{ p: 2, mb: 2, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
        <Box sx={{ flex: '1 1 auto' }}>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
            <Typography variant="h5" fontWeight="bold">{c.name}</Typography>
            {c.tier && <Chip size="small" color="warning" label={c.tier} />}
            {c.isTestAccount && <Chip size="small" color="error" label="บัญชีทดสอบ · ไม่นับในรายงาน" />}
            <Chip size="small" color={c.lineFriend ? 'success' : 'default'} label={c.lineFriend ? 'เพื่อน LINE' : 'ไม่ได้เป็นเพื่อน LINE'} />
          </Box>
          <Typography variant="body2" color="text.secondary">
            <Box component="span" sx={{ fontFamily: 'monospace' }}>{c.tamaCode || `#${c.userId}`}</Box>
            {' · '}สมัคร {date(c.joinedAt)}{c.channel ? ` · ช่องทาง ${c.channel}` : ''}{c.language ? ` · ภาษา ${c.language}` : ''}
            {' · '}ใช้งานล่าสุด {dateTime(c.lastActiveAt)}
          </Typography>
        </Box>
        {c.smileName && (
          <Button variant="contained" target="_blank" rel="noopener noreferrer"
            href={`/customer-profile?smileName=${encodeURIComponent(c.smileName)}${c.shop ? `&shop=${encodeURIComponent(c.shop)}` : ''}`}>
            เปิดโปรไฟล์ลูกค้า
          </Button>
        )}
        <Button variant="outlined" color={c.isTestAccount ? 'primary' : 'error'} onClick={toggleTestAccount} disabled={saving}>
          {c.isTestAccount ? 'นับในรายงาน' : 'ซ่อนจากรายงาน (บัญชีทดสอบ)'}
        </Button>
      </Paper>

      <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', mb: 2 }}>
        <Kpi label="ยอดขายทั้งหมด" value={baht(lifetime.total)} sub={`หลังหักคูปอง ${baht(lifetime.afterCoupons)}`} />
        <Kpi label="ออเดอร์ทั้งหมด" value={num(lifetime.orders)} sub={`เฉลี่ย ${baht(lifetime.orders ? lifetime.afterCoupons / lifetime.orders : 0)} หลังคูปอง`} />
        <Kpi label="ส่วนลดคูปองทั้งหมด" value={`−${baht(lifetime.coupons)}`} />
        <Kpi label="ค่าแปลภาษา (ช่วงนี้)" value={usdAsBaht(tr.costUsd)} sub={`เสียเงิน ${num(tr.paid)} จาก ${num(tr.events)} ครั้ง · ${FX_LABEL}`} />
        <Kpi label="ยอดเงินใน wallet" value={baht(wallet.balance)} />
      </Box>

      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
        <Paper variant="outlined" sx={{ p: 2, flex: '1 1 520px', minWidth: 0 }}>
          <Typography fontWeight="bold" mb={0.5}>การใช้งาน</Typography>
          <Typography variant="caption" color="text.secondary" display="block" mb={2}>{data.from} → {data.to}</Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 2 }}>
            <Mini label="ค้นหา (Mercari)" value={num(usage.searches)} sub={`${num(usage.searchTerms)} คำค้น`} />
            <Mini label="ดูหน้าสินค้า (Mercari)" value={num(usage.itemViews)} />
            <Mini label="รายการโปรด" value={num(usage.favourites)} sub="ทั้งหมด" />
            <Mini label="แปลภาษา" value={num(tr.events)} sub={`ฟรี ${num(tr.free)} · คลัง ${num(tr.cache)} · เสียเงิน ${num(tr.paid)}`} />
            <Mini label="คำขอซื้อ" value={num(usage.purchaseRequests)} sub={`ยืนยันแล้ว ${num(usage.purchaseRequestsConfirmed)}`} />
            <Mini label="ลิงก์สั่งซื้อ" value={num(usage.customLinks)} sub={`จ่ายแล้ว ${num(usage.customLinksPaid)}`} />
            <Mini label="คำขอไป Mercari" value={num(usage.mercariRequests.requests)} sub={`ถูกจำกัด (429) ${num(usage.mercariRequests.rateLimited)}`} />
            <Mini label="ต่อราคา" value={num(usage.negotiations)} />
          </Box>
          <Box sx={{ mt: 2, pt: 2, borderTop: 1, borderColor: 'divider' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="body2">สิทธิ์ต่อราคา (ตอนนี้)</Typography>
              <Typography variant="body2" color="text.secondary">
                {quota.unlimited ? `ใช้ ${num(quota.used)} · ไม่จำกัด` : `ใช้ ${num(quota.used)} จาก ${num(quota.limit)}`}
              </Typography>
            </Box>
            {!quota.unlimited && (
              <LinearProgress variant="determinate" value={quota.limit ? Math.min(100, (quota.used / quota.limit) * 100) : 0} sx={{ height: 8, borderRadius: 1, mt: 0.5 }} />
            )}
          </Box>
          <Box sx={{ mt: 2, pt: 2, borderTop: 1, borderColor: 'divider' }}>
            <Typography variant="body2" fontWeight="bold" mb={1}>คำค้นหายอดนิยม</Typography>
            <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
              {usage.topTerms.length === 0 && <Typography variant="body2" color="text.secondary">—</Typography>}
              {usage.topTerms.map((t) => <Chip key={t.term} size="small" label={`${t.term} ×${num(t.n)}`} />)}
            </Box>
          </Box>
        </Paper>

        <Paper variant="outlined" sx={{ p: 2, flex: '1 1 420px', minWidth: 0 }}>
          <Typography fontWeight="bold" mb={1}>กิจกรรมล่าสุด</Typography>
          {data.timeline.length === 0 && <Typography variant="body2" color="text.secondary">ยังไม่มีกิจกรรม</Typography>}
          {data.timeline.map((e, i) => (
            <Box key={`${e.kind}-${i}`} sx={{ display: 'grid', gridTemplateColumns: '110px 12px 1fr', gap: 1, py: 1, borderTop: i ? 1 : 0, borderColor: 'divider' }}>
              <Typography variant="caption" color="text.secondary">{dateTime(e.at)}</Typography>
              <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: TIMELINE_COLOR[e.kind] || 'grey.500', mt: 0.5 }} />
              <Typography variant="body2">{e.text}</Typography>
            </Box>
          ))}
        </Paper>
      </Box>

      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1, mb: 1 }}>
          <Typography fontWeight="bold">ออเดอร์ (ล่าสุด 50)</Typography>
          <Typography variant="body2" color="text.secondary">
            {num(lifetime.orders)} ออเดอร์ · รวม {baht(lifetime.total)} · หลังหักคูปอง {baht(lifetime.afterCoupons)}
          </Typography>
        </Box>
        <TableContainer>
          <Table size="small" sx={{ minWidth: 1000 }}>
            <TableHead>
              <TableRow>
                <TableCell>วันที่</TableCell>
                <TableCell>ออเดอร์</TableCell>
                <TableCell>ร้านค้า</TableCell>
                <TableCell>ส่งทาง</TableCell>
                <TableCell>สถานะ</TableCell>
                {REVENUE_TYPES.map((t) => <TableCell key={t.key} align="right">{t.label}</TableCell>)}
                <TableCell align="right">รวม</TableCell>
                <TableCell align="right">คูปอง</TableCell>
                <TableCell align="right">หลังคูปอง</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.orders.length === 0 && (
                <TableRow><TableCell colSpan={REVENUE_TYPES.length + 8} align="center">ยังไม่มีออเดอร์</TableCell></TableRow>
              )}
              {data.orders.map((o) => (
                <TableRow key={o.id}>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>{date(o.orderDate)}</TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>{o.orderCode}</Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{o.itemTitle || ''}</Typography>
                  </TableCell>
                  <TableCell>{MARKETPLACE_LABEL[o.marketplace] || o.marketplace}</TableCell>
                  <TableCell>{SHIPPING_LABEL[o.shippingMode] || '—'}</TableCell>
                  <TableCell>
                    <Typography variant="body2">{o.shipStatus}</Typography>
                    <Typography variant="caption" color="text.secondary">{o.productPayment}</Typography>
                  </TableCell>
                  {REVENUE_TYPES.map((t) => (
                    <TableCell key={t.key} align="right" sx={{ color: o.revenue[t.key] ? undefined : 'text.disabled' }}>
                      {o.revenue[t.key] ? baht(o.revenue[t.key]) : '—'}
                    </TableCell>
                  ))}
                  <TableCell align="right">{baht(o.revenue.total)}</TableCell>
                  <TableCell align="right" sx={{ color: o.coupon ? 'warning.dark' : 'text.disabled' }}>{o.coupon ? `−${baht(o.coupon)}` : '—'}</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold' }}>{baht(o.afterCoupons)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
        <Paper variant="outlined" sx={{ p: 2, flex: '1 1 520px', minWidth: 0 }}>
          <Typography fontWeight="bold" mb={1}>คูปอง</Typography>
          <TableContainer>
            <Table size="small" sx={{ minWidth: 560 }}>
              <TableHead>
                <TableRow>
                  <TableCell>คูปอง</TableCell>
                  <TableCell>ได้รับ</TableCell>
                  <TableCell>สถานะ</TableCell>
                  <TableCell>ใช้กับออเดอร์</TableCell>
                  <TableCell align="right">ส่วนลด</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.coupons.length === 0 && (
                  <TableRow><TableCell colSpan={5} align="center">ไม่มีคูปอง</TableCell></TableRow>
                )}
                {data.coupons.map((cp, i) => (
                  <TableRow key={`${cp.id}-${i}`}>
                    <TableCell>
                      <Typography variant="body2" fontWeight="bold">{cp.name}</Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>{cp.code}</Typography>
                    </TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      {date(cp.createdAt)}
                      <Typography variant="caption" display="block" color="text.secondary">{cp.pointCost ? `แลก ${num(cp.pointCost)} แต้ม` : 'ได้รับฟรี'}</Typography>
                    </TableCell>
                    <TableCell>{COUPON_STATUS[cp.status] || cp.status}</TableCell>
                    <TableCell>{cp.orderCode ? `${cp.orderCode}${cp.usedAt ? ` · ${date(cp.usedAt)}` : ''}` : '—'}</TableCell>
                    <TableCell align="right" sx={{ color: cp.discount ? 'warning.dark' : 'text.disabled' }}>{cp.discount ? `−${baht(cp.discount)}` : '—'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>

        <Paper variant="outlined" sx={{ p: 2, flex: '1 1 420px', minWidth: 0 }}>
          <Typography fontWeight="bold" mb={1}>Wallet (ล่าสุด 20)</Typography>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>วันที่</TableCell>
                  <TableCell>ประเภท</TableCell>
                  <TableCell>รายละเอียด</TableCell>
                  <TableCell align="right">จำนวน</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {wallet.transactions.length === 0 && (
                  <TableRow><TableCell colSpan={4} align="center">ไม่มีรายการ</TableCell></TableRow>
                )}
                {wallet.transactions.map((t) => (
                  <TableRow key={t.id}>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{dateTime(t.createdAt)}</TableCell>
                    <TableCell>{WALLET_TYPE[t.type] || t.type}</TableCell>
                    <TableCell sx={{ color: 'text.secondary', wordBreak: 'break-word' }}>{t.description || '—'}</TableCell>
                    <TableCell align="right">{baht(t.amount)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </Box>
    </Box>
  );
};

export default PlatformCustomerDetail;
